// FIX-CCS-OUTBOX (spec §7.2–§7.5) — the outbox for the whole app.
//
// Mounted once inside AuthProvider. It owns the sync triggers (app open, back online, login, the
// camera / Photos screens opening, "Send now"), the owner-bound summary the screens show, and the
// farmer notices (sign in to send, offline sending not enabled, expired photos, iPhone tip).
// The runner itself is runOutbox (syncRunner.ts); this file only wires it to the browser and React.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/components/AuthContext';
import { OFFLINE_PHOTO_POLICY_QUERY_KEY, useOfflinePhotoPolicy } from '@/shared/hooks/useOfflinePhotoPolicy';
import { SNAPSHOT_REFRESH_MS, SNAP_QUERY_PREFIX } from '@/shared/hooks/snapshot/useSnapshotQuery';
import { idbOutboxStore, isOutboxAvailable, type OutboxStore } from './outboxStore';
import { LOCK_TTL_MS, acknowledgeExpired, retryItem, runOutbox, type RunResult, type RunTrigger } from './syncRunner';
import { sendOutboxItem } from './sendItem';
import { isQueueFull, summarise } from './limits';
import { enqueuePhoto, isIosBrowserTab, mergePrecapture, readSpaceEstimate, requestPersistentStorageOnce, type EnqueueInput, type EnqueueResult } from './enqueue';
import type { OutboxItem, OutboxSummary } from './types';
import { OUTBOX_TEST_BREAKS_ON, testBreak } from './testBreaks';
import { pollForFinding } from '@/features/capture/lib/analysisPoll';
// Community Fixes 0710 P2 fix (SD-T731): the ONE env module (Part 0, trap rule 9) — no Live URL/key fallback here.
import { SUPABASE_URL, SUPABASE_ANON_KEY as ANON_KEY } from '@/shared/config/supabaseEnv';
import { getLocationEngine } from '@/features/location/locationEngine';

/** A pending or failed photo of the signed-in farmer, for the Photos screen. */
export interface OutboxPhotoView {
  photoId: string;
  state: OutboxItem['state'];
  previewUrl: string | null;
  category: string;
  queuedAt: string;
  lastError: OutboxItem['last_error'];
  /** G-A unit 4 (R2): the pick-time location this photo carries — the tile always says which (never blank). */
  location: OutboxLocationKind;
}

export type OutboxLocationKind = 'verified' | 'unverified' | 'none' | 'pending';

/** R2 on a stored item: a fix at most freshness_s old at the pick may be verified; an older one is not; none is none. */
export function outboxLocationKind(item: Pick<OutboxItem, 'location_state' | 'location_bundle'>, freshnessS: number): OutboxLocationKind {
  if (item.location_state === 'holding') return 'pending';
  const b = item.location_bundle as { source?: unknown; fix_age_s?: unknown } | null | undefined;
  if (!b || typeof b.source !== 'string') return 'none';
  return b.source === 'browser_watch' && typeof b.fix_age_s === 'number' && b.fix_age_s <= freshnessS ? 'verified' : 'unverified';
}

export type OutboxNotice = 'auth_needed' | 'offline_disabled' | null;

export interface OutboxContextValue {
  /** IndexedDB usable AND the kill switch is on for this farmer: Save goes through the outbox. */
  enabled: boolean;
  available: boolean;
  summary: OutboxSummary;
  photos: OutboxPhotoView[];
  online: boolean;
  running: boolean;
  notice: OutboxNotice;
  /** Mine, expired and not yet acknowledged (R1: tell the farmer, then remove). */
  expiredCount: number;
  queueFull: boolean;
  /** Show the one-time "Add to Home Screen" tip now. */
  showIosTip: boolean;
  dismissIosTip: () => void;
  save: (input: Omit<EnqueueInput, 'store' | 'userId' | 'estimate'>) => Promise<EnqueueResult>;
  attachPrecapture: (photoId: string, precapture: unknown) => Promise<void>;
  /** FIX-CCS-G-GPS: a held photo's location resolved — written once (holding → final), then the item may send. */
  attachLocation: (photoId: string, bundle: Record<string, unknown> | null) => Promise<boolean>;
  run: (trigger: RunTrigger, opts?: { savedPhotoId?: string; force?: boolean }) => Promise<RunResult | null>;
  retry: (photoId: string) => Promise<void>;
  acknowledgeExpired: () => Promise<void>;
  refresh: () => Promise<void>;
}

const EMPTY: OutboxSummary = { pending: 0, failed: 0, otherAccounts: 0, bytes: 0, totalItems: 0, totalBytes: 0 };
const OutboxCtx = createContext<OutboxContextValue | null>(null);

function tabHolderId(): string {
  try {
    return `tab-${crypto.randomUUID()}`;
  } catch {
    return `tab-${Math.random().toString(36).slice(2)}-${Date.now()}`;
  }
}

export const OutboxProvider: React.FC<{ children: React.ReactNode; store?: OutboxStore }> = ({ children, store = idbOutboxStore }) => {
  const { user } = useAuth();
  const policy = useOfflinePhotoPolicy();
  const queryClient = useQueryClient();
  const userRef = useRef(user);
  userRef.current = user;
  const policyRef = useRef(policy);
  policyRef.current = policy;
  const holderId = useRef(tabHolderId()).current;

  const [available, setAvailable] = useState(false);
  const [items, setItems] = useState<OutboxItem[]>([]);
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [running, setRunning] = useState(false);
  const [notice, setNotice] = useState<OutboxNotice>(null);
  const [showIosTip, setShowIosTip] = useState(false);
  const runningRef = useRef<Promise<RunResult | null> | null>(null);
  const busyRetryRef = useRef<number | null>(null);
  const runRef = useRef<((trigger: RunTrigger) => Promise<RunResult | null>) | null>(null);

  useEffect(() => {
    let alive = true;
    isOutboxAvailable().then((ok) => alive && setAvailable(ok));
    return () => {
      alive = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!available) return;
    try {
      setItems(await store.all());
    } catch (e) {
      console.warn('[outbox] read failed', e);
    }
  }, [available, store]);

  // Preview object URLs for the signed-in farmer's own items only (owner binding: others are never shown).
  const [photos, setPhotos] = useState<OutboxPhotoView[]>([]);
  useEffect(() => {
    const uid = user?.id ?? null;
    const mine = items.filter((i) => uid && i.owner_user_id === uid && i.state !== 'expired');
    const urls: string[] = [];
    setPhotos(
      mine.map((i) => {
        const url = i.preview ? URL.createObjectURL(i.preview) : null;
        if (url) urls.push(url);
        return {
          photoId: i.photo_id, state: i.state, previewUrl: url, category: i.category, queuedAt: i.queued_at, lastError: i.last_error,
          location: outboxLocationKind(i, getLocationEngine().getState().params.N_s),
        };
      }),
    );
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [items, user?.id]);

  const getSession = useCallback(() => {
    const u = userRef.current;
    const token = (window as unknown as { __AGXL_SESSION_TOKEN__?: string | null }).__AGXL_SESSION_TOKEN__ ?? u?.sessionToken ?? null;
    return u?.id && token ? { userId: u.id, token } : null;
  }, []);

  const confirmRow = useCallback(async (photoId: string): Promise<boolean | null> => {
    try {
      const { data, error } = await supabase.from('photos').select('id').eq('id', photoId).limit(1);
      if (error) return null;
      return (data?.length ?? 0) > 0;
    } catch {
      return null;
    }
  }, []);

  const run = useCallback(
    async (trigger: RunTrigger, opts: { savedPhotoId?: string; force?: boolean } = {}): Promise<RunResult | null> => {
      if (!available) return null;
      // No signal: a send can only fail. The 'online' event starts the next run (R5: any connection will do).
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return null;
      if (runningRef.current) {
        // One run per tab at a time; a save during a run waits for it, then runs for its own photo.
        await runningRef.current.catch(() => null);
        if (trigger !== 'save' && trigger !== 'send_now' && trigger !== 'retry') return null;
      }
      const p = (async () => {
        setRunning(true);
        try {
          const res = await runOutbox(
            {
              store,
              holderId,
              getSession,
              confirmRow,
              policyEnabled: () => policyRef.current.enabled,
              liveHolds: () => getLocationEngine().pendingIds(),
              send: (item, uploadSource, token, atSave) =>
                sendOutboxItem(item, { supabaseUrl: SUPABASE_URL, anonKey: ANON_KEY, sessionToken: token, uploadSource, atSave }),
              log: (m, d) => console.info(`[outbox] ${m}`, d ?? ''),
            },
            { trigger, savedPhotoId: opts.savedPhotoId, force: opts.force },
          );
          if (res.stop === 'busy') {
            // Another tab (or a page that was just closed mid-run) holds the lock; it expires within LOCK_TTL_MS.
            if (busyRetryRef.current) window.clearTimeout(busyRetryRef.current);
            busyRetryRef.current = window.setTimeout(() => {
              busyRetryRef.current = null;
              runRef.current?.('app_open');
            }, LOCK_TTL_MS + 2_000);
          }
          if (res.stop === 'auth_needed') setNotice('auth_needed');
          else if (res.stop === 'offline_disabled') setNotice('offline_disabled');
          else if (res.sent.length || res.duplicates.length) setNotice(null);
          if (OUTBOX_TEST_BREAKS_ON && testBreak('poll')) {
            // Dev rig deliberate fail only: poll for the finding after queued sends too (the app never does this).
            for (const id of res.sent) if (id !== opts.savedPhotoId) pollForFinding(id, async (pid) => (await supabase.from('photos').select('ai_analysis').eq('id', pid).limit(1)).data?.[0]?.ai_analysis ?? null);
          }
          if (res.sent.length || res.duplicates.length) {
            const uid = userRef.current?.id;
            queryClient.invalidateQueries({ queryKey: ['photos'] });
            queryClient.invalidateQueries({ queryKey: ['activities'] });
            if (uid) queryClient.invalidateQueries({ queryKey: ['credit_score', uid] });
            try {
              localStorage.removeItem('agxl_recent_photos_cache');
            } catch {
              /* ignore */
            }
          }
          return res;
        } catch (e) {
          console.warn('[outbox] run failed', e);
          return null;
        } finally {
          setRunning(false);
          await refresh();
        }
      })();
      runningRef.current = p;
      try {
        return await p;
      } finally {
        if (runningRef.current === p) runningRef.current = null;
      }
    },
    [available, store, holderId, getSession, confirmRow, queryClient, refresh],
  );

  runRef.current = run;

  // Leaving the page: give the runner lock back at once (best effort) so the next page does not wait for it to expire.
  useEffect(() => {
    const release = () => {
      store.releaseLock(holderId).catch(() => undefined);
    };
    window.addEventListener('pagehide', release);
    return () => {
      window.removeEventListener('pagehide', release);
      if (busyRetryRef.current) window.clearTimeout(busyRetryRef.current);
    };
  }, [store, holderId]);

  // Triggers: app open (and store ready), login / farmer change, back online. A retry timer follows the
  // earliest back-off so a phone left open keeps trying without a tap.
  useEffect(() => {
    if (!available) return;
    refresh();
    if (user?.id) {
      setNotice(null);
      run('login');
      requestPersistentStorageOnce(store);
    }
  }, [available, user?.id, store, refresh, run]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const on = () => {
      setOnline(true);
      // SD-O41: a recovered radio refills the screens without a reload — reads paused on the dead radio do not
      // resume by themselves, so the farmer's keys are invalidated here (the one 'online' listener of the app).
      for (const queryKey of [['photos'], ['credit_score'], ['active-cycle-context']]) queryClient.invalidateQueries({ queryKey });
      // SD-O49: the kill-switch verdict is re-read on reconnect too, so a flip reaches the phone within the hour.
      queryClient.invalidateQueries({ queryKey: [OFFLINE_PHOTO_POLICY_QUERY_KEY] });
      refreshStaleSnapshots();
      run('online');
    };
    const off = () => setOnline(false);
    // SD-O49: app resume (the tab or PWA comes back to the foreground) re-reads the verdict as well.
    const visible = () => {
      if (document.visibilityState === 'visible') {
        queryClient.invalidateQueries({ queryKey: [OFFLINE_PHOTO_POLICY_QUERY_KEY] });
        refreshStaleSnapshots();
      }
    };
    // P1 OD-SNAPSHOT: snapshot-backed reads older than 1 h are refetched on reconnect and on resume.
    function refreshStaleSnapshots() {
      queryClient.invalidateQueries({ queryKey: [SNAP_QUERY_PREFIX], predicate: (q) => Date.now() - q.state.dataUpdatedAt > SNAPSHOT_REFRESH_MS });
    }
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    document.addEventListener('visibilitychange', visible);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [run, queryClient]);

  useEffect(() => {
    if (!available || !user?.id || !online) return;
    const mine = items.filter((i) => i.owner_user_id === user.id && i.state === 'pending' && i.next_attempt_at);
    if (mine.length === 0) return;
    const next = Math.min(...mine.map((i) => Date.parse(i.next_attempt_at as string)));
    const wait = Math.max(1000, next - Date.now());
    const h = window.setTimeout(() => run('online'), Math.min(wait, 10 * 60 * 1000));
    return () => window.clearTimeout(h);
  }, [available, items, user?.id, online, run]);

  const save = useCallback(
    async (input: Omit<EnqueueInput, 'store' | 'userId' | 'estimate'>): Promise<EnqueueResult> => {
      const uid = userRef.current?.id;
      if (!uid) throw new Error('not signed in');
      const estimate = await readSpaceEstimate();
      const res = await enqueuePhoto({ ...input, store, userId: uid, estimate });
      await refresh();
      if (res.ok && isIosBrowserTab()) {
        const shown = await store.getMeta<boolean>('ios_tip_shown').catch(() => true);
        if (!shown) {
          setShowIosTip(true);
          await store.setMeta('ios_tip_shown', true).catch(() => undefined);
        }
      }
      return res;
    },
    [store, refresh],
  );

  const attachPrecapture = useCallback(
    async (photoId: string, precapture: unknown) => {
      const it = await store.get(photoId);
      // SD-O50: merge onto the stamp written at enqueue (app/device/page survive whether or not this arrives in time).
      if (it && it.state === 'pending' && it.attempts === 0) await store.update(photoId, { precapture: mergePrecapture(it.precapture, precapture) });
    },
    [store],
  );

  const attachLocation = useCallback(
    async (photoId: string, bundle: Record<string, unknown> | null) => {
      const it = await store.get(photoId);
      if (!it || it.location_state !== 'holding') return false; // once only: a final bundle is never replaced
      await store.update(photoId, { location_state: 'final', location_bundle: bundle });
      await refresh();
      return true;
    },
    [store, refresh],
  );

  const retry = useCallback(
    async (photoId: string) => {
      const uid = userRef.current?.id;
      if (!uid) return;
      if (await retryItem(store, photoId, uid)) await run('retry', { force: true });
      else await refresh();
    },
    [store, run, refresh],
  );

  const ack = useCallback(async () => {
    const uid = userRef.current?.id;
    if (!uid) return;
    await acknowledgeExpired(store, uid);
    await refresh();
  }, [store, refresh]);

  const summary = useMemo(() => (available ? summarise(items, user?.id ?? null) : EMPTY), [available, items, user?.id]);
  const expiredCount = useMemo(() => items.filter((i) => i.owner_user_id === user?.id && i.state === 'expired').length, [items, user?.id]);

  const value: OutboxContextValue = {
    enabled: available && policy.enabled,
    available,
    summary,
    photos,
    online,
    running,
    notice,
    expiredCount,
    queueFull: isQueueFull(items),
    showIosTip,
    dismissIosTip: () => setShowIosTip(false),
    save,
    attachPrecapture,
    attachLocation,
    run,
    retry,
    acknowledgeExpired: ack,
    refresh,
  };
  return <OutboxCtx.Provider value={value}>{children}</OutboxCtx.Provider>;
};

const NOOP: OutboxContextValue = {
  enabled: false,
  available: false,
  summary: EMPTY,
  photos: [],
  online: true,
  running: false,
  notice: null,
  expiredCount: 0,
  queueFull: false,
  showIosTip: false,
  dismissIosTip: () => undefined,
  save: async () => {
    throw new Error('outbox not mounted');
  },
  attachPrecapture: async () => undefined,
  attachLocation: async () => false,
  run: async () => null,
  retry: async () => undefined,
  acknowledgeExpired: async () => undefined,
  refresh: async () => undefined,
};

export function useOutbox(): OutboxContextValue {
  return useContext(OutboxCtx) ?? NOOP;
}

/** Camera and Photos screens: opening the screen is a sync trigger (spec §7.3). */
export function useOutboxScreenTrigger(): void {
  const { run, available } = useOutbox();
  useEffect(() => {
    if (available) run('screen_open');
  }, [available]); // eslint-disable-line react-hooks/exhaustive-deps
}
